import { useEffect } from 'react';

/**
 * The `?copie` of §4.2 (issue #116): the flag a duplication carries to the
 * editor it opens, so that editor SELECTS its name field.
 *
 * Written once here and read by the four editor routes, because it is the same
 * parse four times over - and the fourth copy is how the drift starts: the day
 * the flag gains a second accepted spelling, or TanStack changes how it
 * serialises a boolean, one of four would be left behind.
 *
 * ## In the URL and not in a router state
 *
 * The reason « Voir » puts its version there: a gesture that navigates has to
 * survive the reload the navigation may meet, and a parameter is the only thing
 * that does. The worst a stale `?copie` can do is select a name somebody was
 * about to type over anyway - and it does not get the chance, because
 * `useConsumedCopyFlag` below takes it out of the address bar as soon as it has
 * been read.
 */

/** Present and true, or absent. Anything else degrades to « no ». */
export function readCopyFlag(raw: unknown): true | undefined {
  return raw === true || raw === 'true' ? true : undefined;
}

/**
 * Drops the flag from the address bar once the editor under it has opened.
 *
 * It matters because the editor is remounted by more than a duplication: its
 * `key` carries the counter « Recharger le brouillon du serveur » bumps. Left
 * in the URL, the flag would re-select the name field on that remount and on
 * every browser reload - stealing the caret from somebody who had moved on,
 * which is the one thing the selection exists to avoid doing.
 *
 * `drop` navigates with `replace`, so the flag leaves no entry in the back
 * stack: it is a hand-off between two screens and not a place anybody returns
 * to. It fires AFTER the editor's own effect, which is what React guarantees -
 * a child's effects run before its parent's - so the selection has already
 * happened when the parameter goes.
 */
export function useConsumedCopyFlag(
  copie: true | undefined,
  /**
   * Whether the editor the flag is FOR has mounted.
   *
   * Not a refinement: the editor reads the flag on ITS OWN mount, and it does
   * not mount until the entity has been read. Dropped on the route's first
   * commit - which is a spinner - the flag would be gone before anything could
   * act on it, and the name would never be selected.
   *
   * Which is what makes this a parameter rather than a comment: a duplication
   * from a CARD does not seed the copy's cache entry, so the editor waits a
   * round trip, and a flag dropped early is a gesture that never selects. The
   * head of the editor used to seed that entry and resolve at once, which hid
   * the defect - that button is gone since #123, so the card is the only path.
   */
  opened: boolean,
  drop: () => void,
): void {
  useEffect(() => {
    // Guarded rather than run once: `drop` is written inline at every call
    // site, so the effect re-runs on each render - and the guard is false from
    // the render that follows the navigation, which is what ends it.
    if (copie === true && opened) drop();
  }, [copie, opened, drop]);
}
